import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Bell, MessageSquare, CheckCheck } from "lucide-react";
import { Button } from "@/components/ui/button";

export default function NotificationBell({ onMessages }) {
    const nav = useNavigate();
    const [items, setItems] = useState([]);
    const [count, setCount] = useState(0);
    const [msgUnread, setMsgUnread] = useState(0);
    const [open, setOpen] = useState(false);

    const load = async () => {
        try {
            const [nc, mc, list] = await Promise.all([
                api.get("/notifications/unread-count"),
                api.get("/messages/unread-count"),
                api.get("/notifications"),
            ]);
            setCount(nc.data.unread || 0);
            setMsgUnread(mc.data.unread || 0);
            setItems(list.data || []);
        } catch {}
    };

    useEffect(() => {
        load();
        const iv = setInterval(load, 15000);
        return () => clearInterval(iv);
    }, []);

    const openTopic = async (n) => {
        try { await api.post(`/notifications/${n.id}/read`); } catch {}
        setOpen(false);
        load();
        nav(`/student/topic/${n.topic_id}`);
    };

    const readAll = async () => { try { await api.post("/notifications/read-all"); load(); } catch {} };

    return (
        <div className="flex items-center gap-2">
            <Button
                variant="ghost"
                size="sm"
                onClick={onMessages}
                data-testid="btn-header-messages"
                className="relative text-white/80 hover:text-white hover:bg-white/10 h-9"
            >
                <MessageSquare className="w-4 h-4" />
                <span className="mr-1 text-sm">الرسائل</span>
                {msgUnread > 0 && (
                    <span data-testid="msg-badge" className="absolute -top-1 -left-1 bg-[#C8102E] text-white text-[10px] rounded-full min-w-5 h-5 px-1 flex items-center justify-center font-mono-num">
                        {msgUnread > 99 ? "99+" : msgUnread}
                    </span>
                )}
            </Button>

            <Popover open={open} onOpenChange={setOpen}>
                <PopoverTrigger asChild>
                    <Button variant="ghost" size="sm" data-testid="btn-header-bell" className="relative text-white/80 hover:text-white hover:bg-white/10 h-9">
                        <Bell className="w-4 h-4" />
                        {count > 0 && (
                            <span data-testid="notif-badge" className="absolute -top-1 -left-1 bg-[#C8102E] text-white text-[10px] rounded-full min-w-5 h-5 px-1 flex items-center justify-center font-mono-num">
                                {count > 99 ? "99+" : count}
                            </span>
                        )}
                    </Button>
                </PopoverTrigger>
                <PopoverContent dir="rtl" align="end" className="w-80 p-0">
                    <div className="flex items-center justify-between p-3 border-b border-slate-100">
                        <div className="font-semibold text-sm text-[#0F172A]">الإشعارات</div>
                        {count > 0 && (
                            <button onClick={readAll} data-testid="btn-read-all" className="text-xs text-[#1B2A4E] hover:underline flex items-center gap-1">
                                <CheckCheck className="w-3 h-3" /> تحديد الكل مقروء
                            </button>
                        )}
                    </div>
                    <div className="max-h-96 overflow-y-auto">
                        {items.length === 0 && <div className="text-center text-slate-400 text-sm p-6">لا إشعارات</div>}
                        {items.map((n) => (
                            <button
                                key={n.id}
                                onClick={() => openTopic(n)}
                                data-testid={`notif-${n.id}`}
                                className={`w-full text-right p-3 border-b border-slate-100 hover:bg-slate-50 ${!n.is_read ? "bg-blue-50/40" : ""}`}
                            >
                                <div className="flex items-start gap-2">
                                    {!n.is_read && <span className="w-2 h-2 rounded-full bg-[#C8102E] mt-2 shrink-0" />}
                                    <div className="flex-1 min-w-0">
                                        <div className="text-xs text-slate-500">
                                            {n.section === "quantitative" ? "قدرات كمية" : "قدرات لفظية"} • موضوع جديد
                                        </div>
                                        <div className="font-medium text-sm text-[#0F172A] truncate">{n.title}</div>
                                        <div className="text-[10px] text-slate-400 mt-0.5 font-mono-num" dir="ltr">
                                            {new Date(n.created_at).toLocaleString("ar-EG")}
                                        </div>
                                    </div>
                                </div>
                            </button>
                        ))}
                    </div>
                </PopoverContent>
            </Popover>
        </div>
    );
}
